import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { useEffect, useMemo } from "react";
import ModelSelector from "../../components/ModelSelector";
import PopOutTextInput from "../../components/PopOutTextInput";
import { VariableField } from "../../components/PopOutTextDialog";

export default ({
  actionParameters,
  setActionParameters,
  currentPlan,
  headers,
  existingRecord,
  setValidForm,
  getToken,
  companyId,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  currentPlan: string;
  headers: Array<any>;
  existingRecord: boolean;
  setValidForm: (b: boolean) => void;
  getToken?: () => Promise<string>;
  companyId?: string;
}) => {
  useEffect(() => {
    setValidForm(actionParameters.goal && actionParameters.model);
  }, [actionParameters]);

  // Build variable fields based on action type
  const variableFields: VariableField[] = useMemo(() => {
    const fields: VariableField[] = [
      { key: "Company Description", label: "Company Description" },
    ];

    if (actionParameters.action === "add_rows") {
      fields.push(
        { key: "last_20_rows", label: "Last 20 Rows" },
        { key: "last_50_rows", label: "Last 50 Rows" },
        { key: "last_100_rows", label: "Last 100 Rows" }
      );
    } else {
      // update_rows
      fields.push({ key: "Full Record", label: "Full Record" });
      headers.forEach((header) => {
        fields.push({ key: header.field, label: header.field });
      });
    }

    return fields;
  }, [actionParameters.action, headers]);

  return (
    <div>
      <div style={{ marginTop: "16px", display: existingRecord ? "none" : "" }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Agent Action
          <Tooltip title="Update Rows: Fill in or modify data for existing rows in your list. Add Rows: Create new rows based on AI-generated content (e.g., find new leads, generate data).">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Choose your agent action. Do you want to fill in information for
          existing rows or add new rows?
        </Typography>
        <FormControl fullWidth style={{ marginTop: 20 }}>
          <InputLabel>Action</InputLabel>
          <Select
            label="Action"
            value={actionParameters.action}
            onChange={(e) =>
              setActionParameters({
                ...actionParameters,
                action: e.target.value,
              })
            }
          >
            <MenuItem value="update_rows">Update Rows</MenuItem>
            <MenuItem value="add_rows">Add Rows</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div style={{ marginTop: "16px" }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Model
          <Tooltip title="GPT-4o and Claude models offer the best quality but cost more. GPT-4o-mini and Claude Haiku are faster and cheaper for simpler tasks. DeepSeek offers good value for complex reasoning.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Choose your model. Some models are more expensive but provide higher
          quality results.
        </Typography>
        <ModelSelector
          currentPlan={currentPlan}
          onChange={(e) =>
            setActionParameters({ ...actionParameters, model: e.target.value })
          }
          value={actionParameters.model}
          tools
        />
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Your Goal
          <Tooltip title="Write specific, step-by-step instructions. Use {{Column Name}} to reference row data. Be explicit about expected output format. Example: 'Visit {{Website}} and find the CEO name, then summarize their background in 2 sentences.'">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Divide the task into smaller steps. Give clear instructions for each
          step, specifying the expected output.
        </Typography>
        <PopOutTextInput
          fullWidth
          multiline
          rows={12}
          placeholder="Navigate to {{Company Website}} 'Careers' page. Check if they're hiring SDRs or Account Executives. If so, summarize the top three responsibilities in 20 words or less."
          variant="outlined"
          value={actionParameters.goal || ""}
          onChange={(value) =>
            setActionParameters({ ...actionParameters, goal: value })
          }
          sx={{ marginTop: "8px" }}
          dialogTitle="Your Goal"
          dialogDescription="Divide the task into smaller steps. Give clear instructions for each step, specifying the expected output."
          popOutRows={25}
          variableFields={variableFields}
          enableSlashTrigger
          getToken={getToken}
          companyId={companyId}
          aiContext="Research agent goal/instructions for a smart list action"
        />
      </div>

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1">Data Sources</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The research agent has access to the following sources to complete
          your goal:
        </Typography>
        <Typography
          variant="body2"
          component="ul"
          sx={{ pl: 3, m: 0, color: "text.secondary" }}
        >
          <li>Web search (search engine results)</li>
          <li>News search</li>
          <li>Website reading &amp; summarization</li>
          <li>Google Maps (local businesses, places &amp; details)</li>
          <li>Google Jobs (job listings)</li>
          <li>Research subject summary (multi-source AI research)</li>
        </Typography>
      </div>
    </div>
  );
};
